// The overload panel's moves (spec 03 §4.6, P3-I-3, D-402): the ranked,
// priced ways to clear an overload, each with its kind's sentence, its effect
// once the runner has scored it ("Scoring…" until then), its money for a
// holder of `planning.cost.read` ("Cost hidden" otherwise) and Apply for a
// holder of `planning.schedule.publish`. Overtime is applied only by a holder
// of `planning.calendar.manage` and `planning.cost.read`, and reads "A
// facility admin applies overtime" for anyone else. Apply asks first, in the
// panel itself, naming what will be written. An applied overload shows the
// move, who applied it and when.

import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useMemberNames } from "../hooks/mps-queries";
import { problemExtra } from "../hooks/planning-queries";
import {
  applyScheduleMove,
  type ScheduleHeader,
  type ScheduleLane,
  type ScheduleMove,
  type ScheduleOverload,
} from "../hooks/schedule-queries";
import { formatHours } from "../lib/calendar-lib";
import { shortId } from "../lib/mrp-lib";
import { useOffline } from "../lib/planning-lib";
import { laneName, zonedStamp } from "../lib/schedule-lib";
import { applyGate, moveMoney } from "../lib/schedule-moves-lib";

/** What an apply ended as, for the board's notice. */
export interface MoveApplied {
  readonly header: ScheduleHeader;
  readonly kind: ScheduleMove["kind"];
}

/** The codes a 403 names, in words. */
const CODE_KEY: Readonly<Record<string, string>> = {
  "planning.calendar.manage": "planning.schedule.moves.code.calendar",
  "planning.cost.read": "planning.schedule.moves.code.cost",
};

function useLaneName(lanes: readonly ScheduleLane[]) {
  return (id: string) =>
    laneName(
      lanes.find((l) => l.work_center_id === id),
      id,
    );
}

/** A move's sentence: what it does, in the reader's words (§4.6). */
export function MoveSentence({
  move,
  lanes,
}: {
  move: ScheduleMove;
  lanes: readonly ScheduleLane[];
}) {
  const { t, i18n } = useTranslation();
  const nameOf = useLaneName(lanes);
  const hours = (minutes: number) => formatHours(minutes, i18n.language);
  switch (move.kind) {
    case "overtime":
      return (
        <>
          {t("planning.schedule.moves.overtime", {
            hours: hours(move.params.intervals.reduce((n, i) => n + i.added_minutes, 0)),
            wc: nameOf(move.params.work_center_id),
          })}
        </>
      );
    case "alternate_work_center":
      return (
        <>
          {t("planning.schedule.moves.alternate", {
            count: move.params.operations.length,
            hours: hours(move.params.moved_minutes),
            from: nameOf(move.params.from_work_center_id),
            to: nameOf(move.params.to_work_center_id),
          })}
        </>
      );
    case "push_order":
      return (
        <>
          {t("planning.schedule.moves.push", {
            order: move.params.label.order_no ?? t("planning.schedule.block.plannedOrder"),
            wc: nameOf(move.params.work_center_id),
            hours: hours(move.params.standard_minutes),
          })}
        </>
      );
  }
}

/** The rows a move writes, listed under its sentence and in its confirmation. */
function MoveDetail({ move, timezone }: { move: ScheduleMove; timezone: string }) {
  const { t, i18n } = useTranslation();
  if (move.kind === "overtime") {
    return (
      <ul className="planning-line-list" data-testid="planning-schedule-move-intervals">
        {move.params.intervals.map((i) => (
          <li key={i.start} data-start={i.start} data-end={i.end}>
            {t("planning.schedule.moves.interval", {
              from: zonedStamp(i.start, i18n.language, timezone),
              to: zonedStamp(i.end, i18n.language, timezone),
            })}
          </li>
        ))}
      </ul>
    );
  }
  if (move.kind === "alternate_work_center") {
    return (
      <ul className="planning-line-list" data-testid="planning-schedule-move-ops">
        {move.params.operations.map((o) => (
          <li key={o.op_ref} data-op-ref={o.op_ref}>
            {t("planning.schedule.moves.op", {
              order: o.label.order_no ?? t("planning.schedule.block.plannedOrder"),
              no: o.step_no,
              minutes: o.minutes,
            })}
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

function MoneyLine({ move }: { move: ScheduleMove }) {
  const { t, i18n } = useTranslation();
  const money = moveMoney(move, i18n.language);
  if (money.kind === "hidden") {
    return (
      <span
        className="planning-hint"
        data-testid="planning-schedule-move-money"
        data-money="hidden"
      >
        {t("planning.schedule.moves.costHidden")}
      </span>
    );
  }
  if (money.kind === "unpriced") {
    return (
      <span
        className="planning-hint"
        data-testid="planning-schedule-move-money"
        data-money="unpriced"
        data-reason={money.reason}
      >
        {t(`planning.schedule.moves.unpriced.${money.reason}`)}
      </span>
    );
  }
  return (
    <span
      data-testid="planning-schedule-move-money"
      data-money="priced"
      data-cost-minor={String(move.cost_minor ?? "")}
      data-currency={move.currency ?? ""}
      data-reason={money.atRiskReason ?? ""}
    >
      <strong>{t("planning.schedule.moves.cost", { cost: money.cost })}</strong>
      {money.atRisk !== null ? (
        <span className="planning-hint">
          {" · "}
          {t("planning.schedule.moves.atRisk", { value: money.atRisk })}
        </span>
      ) : money.atRiskReason !== null ? (
        <span className="planning-hint">
          {" · "}
          {t(`planning.schedule.moves.atRiskUnpriced.${money.atRiskReason}`)}
        </span>
      ) : null}
    </span>
  );
}

/**
 * A move's effect. An unscored move reads "Scoring…" only while its
 * overload's scoring is pending; once the pass has written the overload, a
 * move it did not reach within the twelve re-solves says it was not scored,
 * so the board never promises a figure that will not come (D-418).
 */
function EffectLine({
  move,
  status,
}: {
  move: ScheduleMove;
  status: "pending" | "scored" | "abandoned";
}) {
  const { t } = useTranslation();
  const e = move.effect;
  const scored = e.scored && e.late_orders_after !== null;
  const unscored =
    status === "pending"
      ? t("planning.schedule.moves.effectLatePending", { before: e.late_orders_before })
      : status === "abandoned"
        ? t("planning.schedule.moves.effectLateAbandoned", { before: e.late_orders_before })
        : t("planning.schedule.moves.effectLateUnscored", { before: e.late_orders_before });
  return (
    <span
      className="planning-move-effect"
      data-testid="planning-schedule-move-effect"
      data-scored={String(scored)}
      data-moves-status={status}
      data-before={e.late_orders_before}
      data-after={e.late_orders_after ?? ""}
      data-clears={e.clears === null ? "" : String(e.clears)}
    >
      {scored
        ? t("planning.schedule.moves.effectLate", {
            before: e.late_orders_before,
            after: e.late_orders_after,
          })
        : unscored}
      {scored && e.clears !== null ? (
        <StatusChip variant={e.clears ? "ok" : "neutral"} dot={false}>
          {e.clears ? t("planning.schedule.moves.clears") : t("planning.schedule.moves.notClears")}
        </StatusChip>
      ) : null}
    </span>
  );
}

/**
 * The moves of one overload, for a reader the server serves them to (a
 * holder of `planning.schedule.publish` or `planning.cost.read`, §7
 * narrowing (b)). Rendered inside the overload panel.
 */
export function MovesSection({
  overload,
  lanes,
  timezone,
  has,
  canReadMembers,
  onApplied,
  onRefresh,
  onSuperseded,
}: {
  overload: ScheduleOverload;
  lanes: readonly ScheduleLane[];
  timezone: string;
  has: (code: string) => boolean;
  canReadMembers: boolean;
  onApplied: (applied: MoveApplied) => void;
  onRefresh: () => Promise<void>;
  onSuperseded: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const members = useMemberNames(canReadMembers && overload.status === "move_applied");
  const [confirming, setConfirming] = useState<ScheduleMove | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<ReactNode | undefined>(undefined);
  const moves = [...(overload.proposed_moves ?? [])].sort((a, b) => a.rank - b.rank);
  const status = overload.moves_status ?? "pending";
  const applied = overload.status === "move_applied";
  const nameOf = useLaneName(lanes);

  const apply = async (move: ScheduleMove) => {
    setPending(true);
    setProblem(undefined);
    try {
      const header = await applyScheduleMove(overload.schedule_id, overload.id, move.move_id);
      await onRefresh();
      onApplied({ header, kind: move.kind });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = error instanceof RequestError ? error.code : undefined;
      setConfirming(undefined);
      if (code === "SCHEDULE_SUPERSEDED") {
        await onRefresh();
        onSuperseded();
      } else if (code === "MOVE_ALREADY_APPLIED") {
        await onRefresh();
        setProblem(t("planning.schedule.moves.alreadyApplied"));
      } else if (code === "MOVE_NOT_FOUND") {
        await onRefresh();
        setProblem(t("planning.schedule.moves.notFound"));
      } else if (code === "MOVE_NOT_APPLICABLE") {
        const reason = problemExtra(error, "reason");
        setProblem(
          typeof reason === "string"
            ? t(`planning.schedule.moves.notApplicable.${reason}`)
            : t("planning.schedule.moves.failed", { requestId: requestIdOf(error) ?? "-" }),
        );
      } else if (code === "PERMISSION_DENIED") {
        const codes = problemExtra(error, "required_codes");
        const named = Array.isArray(codes)
          ? codes
              .filter((c): c is string => typeof c === "string")
              .map((c) => (CODE_KEY[c] !== undefined ? t(CODE_KEY[c]) : c))
          : [];
        setProblem(t("planning.schedule.moves.denied", { codes: named.join(", ") }));
      } else {
        setProblem(t("planning.schedule.moves.failed", { requestId: requestIdOf(error) ?? "-" }));
      }
    } finally {
      setPending(false);
    }
  };

  const appliedMove =
    applied && overload.applied_move_id !== null
      ? moves.find((m) => m.move_id === overload.applied_move_id)
      : undefined;

  return (
    <section
      className="planning-drawer-section planning-moves"
      data-testid="planning-schedule-moves"
      data-moves-status={status}
    >
      <h3>{t("planning.schedule.moves.title")}</h3>
      {problem !== undefined ? (
        <Banner kind="bad" data-testid="planning-schedule-moves-problem">
          {problem}
        </Banner>
      ) : null}
      {applied ? (
        <p className="planning-note" data-testid="planning-schedule-move-applied">
          {t("planning.schedule.moves.applied", {
            move:
              appliedMove !== undefined
                ? t(`planning.schedule.moves.kind.${appliedMove.kind}`)
                : shortId(overload.applied_move_id ?? ""),
            who:
              overload.applied_by === null
                ? ""
                : (members.byId.get(overload.applied_by) ?? shortId(overload.applied_by)),
            time: zonedStamp(overload.applied_at, i18n.language, timezone),
          })}
        </p>
      ) : null}
      {status === "pending" && moves.length > 0 ? (
        <p className="planning-hint" role="status" data-testid="planning-schedule-moves-scoring">
          {t("planning.schedule.moves.scoringNote")}
        </p>
      ) : null}
      {status === "abandoned" ? (
        <p className="planning-hint" data-testid="planning-schedule-moves-abandoned">
          {t("planning.schedule.moves.abandoned")}
        </p>
      ) : null}
      {confirming !== undefined ? (
        <div className="planning-move-confirm" data-testid="planning-schedule-move-confirm">
          <h4>{t("planning.schedule.moves.confirmTitle")}</h4>
          <p data-testid="planning-schedule-move-confirm-text" data-kind={confirming.kind}>
            {confirming.kind === "overtime"
              ? t("planning.schedule.moves.confirmOvertime", {
                  wc: nameOf(confirming.params.work_center_id),
                })
              : confirming.kind === "alternate_work_center"
                ? t("planning.schedule.moves.confirmAlternate", {
                    to: nameOf(confirming.params.to_work_center_id),
                  })
                : t("planning.schedule.moves.confirmPush", {
                    order:
                      confirming.params.label.order_no ?? t("planning.schedule.block.plannedOrder"),
                  })}
          </p>
          <MoveDetail move={confirming} timezone={timezone} />
          <div className="planning-actions">
            <Button
              disabled={pending}
              data-testid="planning-schedule-move-back"
              onClick={() => {
                setConfirming(undefined);
              }}
            >
              {t("planning.schedule.moves.back")}
            </Button>
            <Button
              variant="primary"
              busy={pending}
              busyLabel={t("planning.schedule.moves.applying")}
              disabled={offline || pending}
              data-testid="planning-schedule-move-confirm-apply"
              onClick={() => void apply(confirming)}
            >
              {t("planning.schedule.moves.apply")}
            </Button>
          </div>
          {offline ? <p className="planning-note">{t("planning.common.offlineWrites")}</p> : null}
        </div>
      ) : moves.length === 0 ? (
        <p className="planning-hint" data-testid="planning-schedule-moves-none">
          {t("planning.schedule.moves.none")}
        </p>
      ) : (
        <ol className="planning-move-list">
          {moves.map((move) => {
            const gate = applyGate(move.kind, has, { offline, applied });
            return (
              <li
                key={move.move_id}
                className="planning-move"
                data-testid="planning-schedule-move"
                data-move-id={move.move_id}
                data-kind={move.kind}
                data-rank={move.rank}
                data-gate={gate}
              >
                <div className="planning-move-head">
                  <StatusChip variant="neutral" dot={false}>
                    {t("planning.schedule.moves.rank", { rank: move.rank })}
                  </StatusChip>
                  <strong>{t(`planning.schedule.moves.kind.${move.kind}`)}</strong>
                </div>
                <p className="planning-move-sentence" data-testid="planning-schedule-move-sentence">
                  <MoveSentence move={move} lanes={lanes} />
                </p>
                <MoveDetail move={move} timezone={timezone} />
                <EffectLine move={move} status={status} />
                <MoneyLine move={move} />
                <div className="planning-move-actions">
                  {gate === "allowed" || gate === "offline" ? (
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={gate === "offline" || pending}
                      data-testid="planning-schedule-move-apply"
                      onClick={() => {
                        setProblem(undefined);
                        setConfirming(move);
                      }}
                    >
                      {t("planning.schedule.moves.apply")}
                    </Button>
                  ) : gate === "overtime_codes" ? (
                    <span className="planning-hint" data-testid="planning-schedule-move-admin">
                      {t("planning.schedule.moves.overtimeAdmin")}
                    </span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {!has("planning.schedule.publish") && moves.length > 0 && !applied ? (
        <p className="planning-hint" data-testid="planning-schedule-moves-read-only">
          {t("planning.schedule.moves.readOnly")}
        </p>
      ) : null}
      {offline && has("planning.schedule.publish") && confirming === undefined ? (
        <p className="planning-note">{t("planning.common.offlineWrites")}</p>
      ) : null}
    </section>
  );
}
